import { inject, Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiCompany, DigitaiCreateCompany, DigitaiFleet, DigitaiOperator, DigitaiUpdateCompanies, DigitaiUpdateCompanyById } from '@apps/digitai';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/company';
import { FleetService } from '@apps/digitai/fleet';
import { OperatorService } from '@apps/digitai/operator';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';
import { findByIdWithRelationsQuery, meCompanyQuery } from './company.graphql';

@Injectable({
    providedIn: 'root',
})
export class CompanyService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiCompany> | null> = new BehaviorSubject(null);
    companySubject$: BehaviorSubject<DigitaiCompany | null> = new BehaviorSubject(null);
    companiesSubject$: BehaviorSubject<DigitaiCompany[] | null> = new BehaviorSubject(null);
    meCompanySubject$: BehaviorSubject<DigitaiCompany | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiCompany> | null>; } = {};
    companyScoped: { [key: string]: BehaviorSubject<DigitaiCompany | null>; } = {};
    companiesScoped: { [key: string]: BehaviorSubject<DigitaiCompany[] | null>; } = {};

    constructor(
        private readonly graphqlService: GraphQLService,
        private readonly operatorService: OperatorService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiCompany>>
    {
        return this.paginationSubject$.asObservable();
    }

    get company$(): Observable<DigitaiCompany>
    {
        return this.companySubject$.asObservable();
    }

    get companies$(): Observable<DigitaiCompany[]>
    {
        return this.companiesSubject$.asObservable();
    }

    get meCompany$(): Observable<DigitaiCompany>
    {
        return this.meCompanySubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiCompany>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiCompany>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeCompany(scope: string, object: DigitaiCompany): void
    {
        if (this.companyScoped[scope])
        {
            this.companyScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.companyScoped[scope] = new BehaviorSubject(object);
    }

    getScopeCompany(scope: string): Observable<DigitaiCompany>
    {
        if (this.companyScoped[scope]) return this.companyScoped[scope].asObservable();

        this.companyScoped[scope] = new BehaviorSubject(null);
        return this.companyScoped[scope].asObservable();
    }

    setScopeCompanies(scope: string, objects: DigitaiCompany[]): void
    {
        if (this.companiesScoped[scope])
        {
            this.companiesScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.companiesScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeCompanies(scope: string): Observable<DigitaiCompany[]>
    {
        if (this.companiesScoped[scope]) return this.companiesScoped[scope].asObservable();

        this.companiesScoped[scope] = new BehaviorSubject(null);
        return this.companiesScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiCompany>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiCompany>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiCompany;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiCompany;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeCompany(scope, data.object) : this.companySubject$.next(data.object)),
            );
    }

    findByIdWithRelations(
        {
            graphqlStatement = findByIdWithRelationsQuery,
            id = '',
            constraint = {},
            queryPaginateOperators = {},
            constraintPaginateOperators = {},
            queryGetFleets = {},
            constraintGetFleets = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            queryPaginateOperators?: QueryStatement;
            constraintPaginateOperators?: QueryStatement;
            queryGetFleets?: QueryStatement;
            constraintGetFleets?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiCompany;
        digitaiPaginateOperators: GridData<DigitaiOperator>;
        digitaiGetFleets: DigitaiFleet[];
    }>
    {
        // inject fleet service to avoid circular dependency
        const fleetService = inject(FleetService);

        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiCompany;
                digitaiPaginateOperators: GridData<DigitaiOperator>;
                digitaiGetFleets: DigitaiFleet[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryPaginateOperators,
                    constraintPaginateOperators,
                    queryGetFleets,
                    constraintGetFleets,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    if (scope)
                    {
                        this.setScopeCompany(scope, data.object);
                    }
                    else
                    {
                        this.companySubject$.next(data.object);
                    }
                    this.operatorService.paginationSubject$.next(data.digitaiPaginateOperators);
                    fleetService.fleetsSubject$.next(data.digitaiGetFleets);
                }),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiCompany;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiCompany;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeCompany(scope, data.object) : this.companySubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiCompany[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiCompany[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeCompanies(scope, data.objects) : this.companiesSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateCompany;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateCompany[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateCompanyById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateCompanies;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    // Queries additionalApis
    meCompany(
        {
            graphqlStatement = meCompanyQuery,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<{
        digitaiMeCompany: DigitaiCompany;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                digitaiMeCompany: DigitaiCompany;
            }>({
                query    : graphqlStatement,
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    this.meCompanySubject$.next(data.digitaiMeCompany);
                }),
            );
    }
}
